import { PlaceholderOption } from "@/components/rich-editor/rich-editor.types.ts";
import { Search } from "@/components/search/Search.tsx";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function GroupedVariableInsertMenu({
  options,
  onInsert,
}: {
  options: PlaceholderOption[];
  onInsert: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const groups = new Map<string, Map<string, PlaceholderOption[]>>();
  for (const option of options) {
    const group = option.group ?? "";
    const subgroup = option.subgroup ?? "";
    const sections = groups.get(group) ?? new Map<string, PlaceholderOption[]>();
    sections.set(subgroup, [...(sections.get(subgroup) ?? []), option]);
    groups.set(group, sections);
  }

  const renderOption = (option: PlaceholderOption, showContext = false) => (
    <DropdownMenuItem
      key={option.id}
      title={option.title}
      description={
        showContext ? [option.group, option.subgroup].filter(Boolean).join(" · ") : undefined
      }
      onClick={() => {
        onInsert(option.id);
        setOpen(false);
        setSearch("");
      }}
    />
  );
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const matchingOptions = options.filter((option) =>
    [option.title, option.id, option.group, option.subgroup]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalizedSearch)
  );

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
      trigger={
        <Button variant="secondary" rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}>
          {t("common:insert") + "..."}
        </Button>
      }
    >
      <div
        className="px-3"
        onKeyDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t("common:search")}
          aria-label={t("common:search")}
        />
      </div>
      {normalizedSearch
        ? matchingOptions.map((option) => renderOption(option, true))
        : Array.from(groups, ([group, sections]) =>
            group ? (
              <DropdownMenuSub key={group} title={group}>
                {Array.from(sections, ([subgroup, fields]) => (
                  <div key={subgroup}>
                    {subgroup && <DropdownMenuLabel>{subgroup}</DropdownMenuLabel>}
                    {fields.map((option) => renderOption(option))}
                  </div>
                ))}
              </DropdownMenuSub>
            ) : (
              Array.from(sections.values())
                .flat()
                .map((option) => renderOption(option))
            )
          )}
      {normalizedSearch && matchingOptions.length === 0 && (
        <DropdownMenuLabel>{t("common:nothing_found")}</DropdownMenuLabel>
      )}
    </DropdownMenu>
  );
}
